import { useQuery } from "@tanstack/react-query";
import { axiosInstance } from "@follosoft/react-components";

import type { ContractContactsApiResponse } from "../types";

import { paramsSerializer } from "../utils/params-serializer";

import { CONTRACT_CONTACTS_ENDPOINT, CONTRACT_CONTACTS_PER_PAGE } from "./endpoints";
import { occupantQueryKey } from "./query-keys";

/** One contact's subscriptions on one contract: the `contract-contacts` index
 * narrowed to that contact. */
export const fetchOccupantRows = async (
  contractId: string | number,
  contactId: string,
): Promise<ContractContactsApiResponse> => {
  const res = await axiosInstance().get<ContractContactsApiResponse>(CONTRACT_CONTACTS_ENDPOINT, {
    params: {
      per_page: CONTRACT_CONTACTS_PER_PAGE,
      contract_id: contractId,
      filter: [{ field: "contact_id", operator: "=", value: contactId }],
    },
    paramsSerializer,
  });

  return res.data;
};

/**
 * One occupant's subscriptions, fetched for the drawer alone.
 *
 * The drawer owns this rather than reading the list's cache, so opening Edit
 * shows what the server holds right now — the table behind it may have been
 * rendered minutes ago, and a stale destination silently saved back is a lost
 * edit. It also means the drawer needs nothing but the two ids to work.
 */
export const useOccupantService = (
  contractId: string | number,
  contactId: string | undefined,
  enabled: boolean,
) =>
  useQuery<ContractContactsApiResponse>({
    queryKey: occupantQueryKey(contractId, String(contactId ?? "")),
    queryFn: () => fetchOccupantRows(contractId, String(contactId ?? "")),
    enabled: enabled && Boolean(contactId),
    // The drawer hydrates from this once per open; a background refetch landing
    // mid-edit would be discarded anyway.
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
